/**
 *  Component: TagsList
 */
import React, { useEffect, useState } from 'react';

import { Flex } from '@chakra-ui/react';
import { Paper, Table, TableBody, TableRow, TablePagination, TableContainer } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import HashTag from 'models/hashtag';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { StyledTableHead, HeaderTableCell } from 'screens/Team/TeamTasksListView/styles';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import { StyledBorderedPaper, VerticallyAlignedFlexContainer } from 'styles/common';

import { isNetchexLogin } from 'constants/netChexConstants';

import { getCompetencies, updateCompetency, saveCompetency, getOrganizationFunctions } from 'services/admin';
import { showErrorSnackbar } from 'services/snackbar';

import { Typography13, Typography16, Typography17, Typography2 } from 'uiToolkit/Typography';
import { Button } from 'uiToolkitV1/Button';

import { SkeletonContainer } from '../../MeshTags/styles';
import NewEditableCompetencyRow from '../common/NewEditableCompetencyRow';

const OrgWideCompetencies = ({
  tagType,
  tagTypeLabel,
  singularTagTypeLabel,
  tagLabel,
  isEnterpriseAccessOnly,
  allowDescriptionNewline = false,
  openCompetencyUploadSidePanel = () => {},
}) => {
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);

  const [allTags, setAllTags] = useState(null);
  const [tags, setTags] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [addNew, setAddNew] = useState(false);
  const [newTag, setNewTag] = useState(new HashTag());
  const rowsPerPageOptions = [5, 10, 15];
  const teamId = null;
  const dispatch = useDispatch();

  // functions and levels
  const [functions, setFunctions] = useState(null);
  const [loadingFunctions, setLoadingFunctions] = useState(true);
  const fetchData = () => {
    getOrganizationFunctions()
      .then((res) => {
        setFunctions(res);
      })
      .catch((err) => {
        console.error('Error in fetching functions : ', err);
        dispatch(showErrorSnackbar(err.message));
      })
      .finally(() => {
        setLoadingFunctions(false);
      });
  };
  useEffect(() => {
    fetchData();
  }, []);
  // end of functions and levels

  const setPaginatedTags = (page, orgTags, perPage) => {
    if (orgTags) {
      const start = page * perPage;
      const end = start + perPage;
      setTags(orgTags.slice(start, end));
    }
  };

  const getData = (page) => {
    getCompetencies({ teamId: -1, tagType })
      .then((orgTags) => {
        const activeTags = orgTags.filter((tag) => tag.active);
        setAllTags(
          activeTags.map((tag) => ({
            ...tag,
            isEditing: false,
            hasChildTags: true,
          }))
        );
        setCount(activeTags.length);
        setPaginatedTags(page, activeTags, rowsPerPage);
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message)))
      .finally(() => setLoading(false));
  };

  // get tags
  useEffect(() => {
    if (!tagType) return;
    setLoading(true);
    getData(0);
  }, [tagType]);

  // update tags
  const updateTag = (tag) => {
    setLoading(true);
    updateCompetency({ ...tag }, teamId, tagType)
      .then((orgTags) => {
        getData(page);
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message));
      })
      .finally(() => setLoading(false));
  };

  // add tags
  const addTag = (tag) => {
    setLoading(true);
    saveCompetency({ ...tag }, teamId, tagType)
      .then((orgTags) => {
        getData(page);
        removeNewTag();
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message));
      })
      .finally(() => setLoading(false));
  };

  const toggleEditing = (tagId) => {
    removeNewTag();
    setTags(
      tags.map((tag) => {
        if (tag.id === tagId) {
          tag.isEditing = !tag.isEditing;
        } else {
          tag.isEditing = false;
        }
        return tag;
      })
    );
  };

  const handleToggleActive = (e, tag) => {
    // const isActive = e.target.checked;
    // adding only canceling functionality
    const isActive = false;
    updateTag({ ...tag, active: isActive });
  };

  const addNewTag = () => {
    setAddNew(true);
    setTags(
      tags.map((tag) => {
        tag.isEditing = false;
        return tag;
      })
    );
  };

  const removeNewTag = () => {
    setAddNew(false);
    setNewTag(new HashTag());
  };

  const disableExpansion = (tagId) => {
    setTags(
      tags.map((tag) => {
        if (tagId === tag.id) tag.hasChildTags = false;
        return tag;
      })
    );
  };

  const enableExpansion = (tagId) => {
    setAllTags(
      allTags.map((tag) => {
        if (tagId === tag.id) tag.hasChildTags = true;
        return tag;
      })
    );
  };

  return (
    <Container>
      <VerticallyAlignedFlexContainer>
        <Typography16 bottomGutter={1.5}>Organization-wide Competencies</Typography16>
      </VerticallyAlignedFlexContainer>
      <Typography17 bottomGutter={3}>
        Please input the organization-wide competencies that are aligned to org name values and are expected of
        everyone, regardless of their function or role.
      </Typography17>
      <StyledBorderedPaper style={{ marginTop: '1.5rem', overflow: 'auto' }} elevation={0}>
        {loading ? (
          <SkeletonContainer>
            <Skeleton width="60%" />
            <Skeleton width="40%" />
            <Skeleton width="50%" />
          </SkeletonContainer>
        ) : (
          <StyledTableContainer>
            <Table size="small" aria-label="a dense table">
              <HeaderComponent tagLabel={tagLabel} isExpandable={tagType === 'GOAL'} />
              {tags && (
                <TableBody>
                  {tags.map((tag) => (
                    <NewEditableCompetencyRow
                      key={tag.id}
                      tag={tag}
                      onActiveToggle={handleToggleActive}
                      onUpdate={updateTag}
                      isExpandable={tagType === 'GOAL'}
                      isEditing={tag.isEditing}
                      toggleEditing={toggleEditing}
                      disableExpansion={() => disableExpansion(tag.id)}
                      enableExpansion={() => enableExpansion(tag.id)}
                      hasChildTags={tag.hasChildTags}
                      canHaveChildTags
                      allowDescriptionNewline={allowDescriptionNewline}
                      functions={functions}
                      loadingFunctions={loadingFunctions}
                    />
                  ))}
                  {addNew && (
                    <NewEditableCompetencyRow
                      tag={newTag}
                      onCancel={removeNewTag}
                      onUpdate={addTag}
                      isExpandable={tagType === 'GOAL'}
                      isEditing
                      toggleEditing={toggleEditing}
                      enableExpansion={enableExpansion}
                      hasChildTags={false}
                      canHaveChildTags={false}
                      allowDescriptionNewline={allowDescriptionNewline}
                      functions={functions}
                      loadingFunctions={loadingFunctions}
                    />
                  )}
                </TableBody>
              )}
            </Table>
          </StyledTableContainer>
        )}
      </StyledBorderedPaper>
      <StyledBottomBar>
        <ButtonContainer>
          <Flex gap={2}>
            <Button w="180px" onClick={openCompetencyUploadSidePanel}>
              Upload
            </Button>
            <Button w="180px" onClick={addNewTag} disabled={addNew}>
              Add {singularTagTypeLabel}
            </Button>
          </Flex>
        </ButtonContainer>

        <TablePagination
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          rowsPerPageOptions={rowsPerPageOptions}
          onChangePage={(e, newPage) => {
            setPage(newPage);
            setPaginatedTags(newPage, allTags, rowsPerPage);
          }}
          onChangeRowsPerPage={(e) => {
            setRowsPerPage(e.target.value);
            setPaginatedTags(page, allTags, e.target.value);
          }}
          style={{ marginLeft: 'auto' }}
        />
      </StyledBottomBar>
      {/* <CompetencySettingsRow /> */}
    </Container>
  );
};

const HeaderComponent = ({ isExpandable = false, tagLabel }) => (
  <StyledTableHead>
    <TableRow>
      {/* { isExpandable && 
          <HeaderTableCell width='10%'></HeaderTableCell>
        } */}
      {/* <HeaderTableCell width="10%">Active</HeaderTableCell> */}
      <HeaderTableCell paddingtop="0.75rem" paddingBottom="0.75rem" paddingLeft="1.35rem" width="20%">
        <Typography2 color="textSecondary" weight="bold">
          Competency
        </Typography2>
      </HeaderTableCell>
      <HeaderTableCell paddingtop="0.75rem" paddingBottom="0.75rem" paddingLeft="0px" width="30%">
        <Typography2 color="textSecondary" weight="bold">
          Hashtag
        </Typography2>
      </HeaderTableCell>
      <HeaderTableCell paddingtop="0.75rem" paddingBottom="0.75rem" width="40%">
        <Typography2 color="textSecondary" weight="bold">
          Description
        </Typography2>
      </HeaderTableCell>
      <HeaderTableCell paddingtop="0.75rem" paddingBottom="0.75rem" width="10%" />
    </TableRow>
  </StyledTableHead>
);

const Container = styled.div`
  padding: 12px;
  position: relative;
`;

const StyledPaper = styled(Paper)`
  margin-top: 1rem;
`;

const StyledBottomBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
`;

const ButtonContainer = styled.div`
  position: absolute;
  right: 16px;
  top: 16px;
`;
const StyledTableContainer = styled(TableContainer)`
  min-width: 1000px;
`;
export default OrgWideCompetencies;
